import { useEffect, useMemo, useState } from "react"
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import {
  createRule,
  publishRule,
  selectRoutingConsultants,
  updateRule,
} from "@features/lead-routing/leadRouting.slice"
import { showErrorSnackbar } from "@features/ui/uiSlice"
import { LeadRoutingClient } from "@clients/leadRoutingClient"
import { ReplayResultView } from "./replayPreview"
import {
  QTF_TIERS,
  LANGUAGE_LEVELS,
  languageLevelLabel,
  humanizePropertyKey,
} from "./leadRoutingLabels"
import {
  DistributionGroupsEditor,
  EditableGroup,
  fromDistributionGroups,
  toDistributionGroups,
} from "./distributionGroupsEditor"
import {
  DynamicLeadProperty,
  KnownRuleConditionProperty,
  Replay,
  Rule,
  RuleCondition,
  RuleConditionOperator,
  RuleConditionProperty,
  RuleConditionValue,
} from "@features/lead-routing/leadRouting.types"

const PROPERTY_LABELS: Record<KnownRuleConditionProperty, string> = {
  qtf: "QTF (campus score)",
  program: "Program",
  englishLevel: "English level",
  germanLevel: "German level",
  residency: "Residency",
  techExperience: "Tech experience",
  advisorMeeting: "Advisor meeting",
  afaRegistration: "Registered with AfA",
  utm: "UTM",
}

const NUMERIC_PROPERTIES: string[] = ["qtf", "englishLevel", "germanLevel"]

const ENUM_OPTIONS: Partial<
  Record<string, { value: string; label: string }[]>
> = {
  program: [
    { value: "SE", label: "Software (SE)" },
    { value: "DA", label: "Data (DA)" },
    { value: "Cyber", label: "Cybersecurity (Cyber)" },
    { value: "OC", label: "Orientation (OC)" },
  ],
  afaRegistration: [
    { value: "agentur-fuer-arbeit", label: "Agentur für Arbeit" },
    { value: "jobcenter", label: "Jobcenter" },
    { value: "not-yet", label: "Not registered yet" },
  ],
  residency: [
    { value: "citizen", label: "Citizen" },
    { value: "immigration", label: "Non-citizen (immigration)" },
  ],
  techExperience: [
    { value: "None", label: "None" },
    { value: "Basic", label: "Basic" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" },
    { value: "Pro", label: "Pro" },
  ],
  advisorMeeting: [
    { value: "scheduled", label: "Scheduled" },
    { value: "already-had-meeting", label: "Already had meeting" },
    { value: "not-requested", label: "Not requested" },
    { value: "requested-waiting-for-date", label: "Requested, waiting" },
  ],
}

const NUMERIC_OPERATORS: RuleConditionOperator[] = [
  "in",
  "notIn",
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "range",
  "isKnown",
  "isUnknown",
]

const LEVEL_OPERATORS: RuleConditionOperator[] = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "range",
  "isKnown",
  "isUnknown",
]

const ENUM_OPERATORS: RuleConditionOperator[] = [
  "in",
  "notIn",
  "eq",
  "neq",
  "isKnown",
  "isUnknown",
]

// Operators offered for a dynamic property, whose values are free-form strings.
const GENERIC_OPERATORS: RuleConditionOperator[] = [
  "in",
  "notIn",
  "eq",
  "neq",
  "contains",
  "notContains",
  "isKnown",
  "isUnknown",
]

const OPERATORS_BY_PROPERTY: Record<
  KnownRuleConditionProperty,
  RuleConditionOperator[]
> = {
  qtf: NUMERIC_OPERATORS,
  englishLevel: LEVEL_OPERATORS,
  germanLevel: LEVEL_OPERATORS,
  program: ENUM_OPERATORS,
  residency: ENUM_OPERATORS,
  techExperience: ENUM_OPERATORS,
  advisorMeeting: ENUM_OPERATORS,
  afaRegistration: ENUM_OPERATORS,
  utm: ["contains", "notContains", "isKnown", "isUnknown"],
}

// Operators for a property: the curated set for a known one, generic string
// operators for a dynamic one.
function operatorsForProperty(
  property: RuleConditionProperty,
): RuleConditionOperator[] {
  return (
    OPERATORS_BY_PROPERTY[property as KnownRuleConditionProperty] ??
    GENERIC_OPERATORS
  )
}

function propertyLabel(property: RuleConditionProperty): string {
  return (
    PROPERTY_LABELS[property as KnownRuleConditionProperty] ??
    humanizePropertyKey(property)
  )
}

const OPERATOR_LABELS: Record<RuleConditionOperator, string> = {
  in: "is any of",
  notIn: "is none of",
  eq: "is",
  neq: "is not",
  gt: "greater than",
  gte: "greater than or equal to",
  lt: "less than",
  lte: "less than or equal to",
  range: "between",
  contains: "contains",
  notContains: "doesn't contain",
  isKnown: "is known",
  isUnknown: "is unknown",
}

// Bounded numeric properties get a dropdown for every operator (not only the
// multi-select ones), we know the exact set of options, so never show a raw
// number field for these.
const LANGUAGE_LEVEL_VALUES = LANGUAGE_LEVELS.map((level) => level.value)

const TIER_OPTIONS: Partial<Record<RuleConditionProperty, number[]>> = {
  qtf: QTF_TIERS,
  englishLevel: LANGUAGE_LEVEL_VALUES,
  germanLevel: LANGUAGE_LEVEL_VALUES,
}

const tierLabel = (property: RuleConditionProperty, tier: number): string =>
  property === "qtf" ? String(tier) : languageLevelLabel(tier)

interface EditableCondition {
  property: RuleConditionProperty
  operator: RuleConditionOperator
  value: RuleConditionValue
}

function isNumericProperty(property: RuleConditionProperty): boolean {
  return NUMERIC_PROPERTIES.includes(property)
}

function needsValue(operator: RuleConditionOperator): boolean {
  return operator !== "isKnown" && operator !== "isUnknown"
}

function defaultValueForOperator(
  operator: RuleConditionOperator,
): RuleConditionValue {
  if (operator === "in" || operator === "notIn") {
    return []
  }
  if (operator === "range") {
    return { min: 0, max: 0 }
  }
  return ""
}

// Keep the current value when the operator changes, adapting its shape so a
// selection isn't wiped (scalar <-> array <-> range): the dropdown stays usable.
function coerceValueForOperator(
  value: RuleConditionValue,
  operator: RuleConditionOperator,
): RuleConditionValue {
  if (operator === "isKnown" || operator === "isUnknown") {
    return ""
  }
  if (operator === "range") {
    return typeof value === "object" && !Array.isArray(value)
      ? value
      : { min: 0, max: 0 }
  }
  if (operator === "in" || operator === "notIn") {
    if (Array.isArray(value)) {
      return value
    }
    if (typeof value === "string" || typeof value === "number") {
      return value === "" ? [] : [value]
    }
    return []
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : ""
  }
  return typeof value === "string" || typeof value === "number" ? value : ""
}

function conditionComplete(condition: EditableCondition): boolean {
  if (!needsValue(condition.operator)) {
    return true
  }
  if (condition.operator === "range") {
    const range = condition.value
    return (
      typeof range === "object" &&
      !Array.isArray(range) &&
      typeof range.min === "number" &&
      typeof range.max === "number"
    )
  }
  if (condition.operator === "in" || condition.operator === "notIn") {
    return Array.isArray(condition.value) && condition.value.length > 0
  }
  return (
    condition.value !== undefined &&
    condition.value !== null &&
    condition.value !== ""
  )
}

function parseCondition(condition: EditableCondition): RuleCondition {
  const value = needsValue(condition.operator) ? condition.value : ""
  return {
    property: condition.property,
    operator: condition.operator,
    value: value ?? "",
  }
}

function ConditionValueInput(props: {
  condition: EditableCondition
  dynamicValues?: string[]
  onChange: (value: RuleConditionValue) => void
}) {
  const { condition, dynamicValues, onChange } = props
  const { property, operator, value } = condition

  if (!needsValue(operator)) {
    return null
  }

  if (operator === "range") {
    const range =
      typeof value === "object" && !Array.isArray(value)
        ? value
        : { min: 0, max: 0 }
    return (
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label="min"
          type="number"
          value={range.min}
          onChange={(e) =>
            onChange({ min: Number(e.target.value), max: range.max })
          }
          sx={{ width: 90 }}
        />
        <TextField
          size="small"
          label="max"
          type="number"
          value={range.max}
          onChange={(e) =>
            onChange({ min: range.min, max: Number(e.target.value) })
          }
          sx={{ width: 90 }}
        />
      </Stack>
    )
  }

  const enumOptions = ENUM_OPTIONS[property]
  const isMulti = operator === "in" || operator === "notIn"

  if (enumOptions) {
    const selected = Array.isArray(value) ? value.map(String) : []
    if (isMulti) {
      return (
        <Autocomplete
          multiple
          size="small"
          options={enumOptions}
          getOptionLabel={(option) => option.label}
          value={enumOptions.filter((option) =>
            selected.includes(option.value),
          )}
          onChange={(_, options) => onChange(options.map((o) => o.value))}
          renderInput={(params) => (
            <TextField {...params} label="Values" placeholder="Select…" />
          )}
          sx={{ minWidth: 240, flex: 1 }}
        />
      )
    }
    return (
      <TextField
        select
        size="small"
        label="Value"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        sx={{ minWidth: 200, flex: 1 }}
      >
        {enumOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  const tiers = TIER_OPTIONS[property]
  if (tiers) {
    if (isMulti) {
      const selectedNums = Array.isArray(value) ? value.map(Number) : []
      return (
        <Autocomplete
          multiple
          size="small"
          options={tiers}
          getOptionLabel={(option) => tierLabel(property, option)}
          value={tiers.filter((tier) => selectedNums.includes(tier))}
          onChange={(_, selectedTiers) => onChange(selectedTiers)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={property === "qtf" ? "QTF tiers" : "Levels"}
              placeholder="Select…"
            />
          )}
          sx={{ minWidth: 200, flex: 1 }}
        />
      )
    }
    return (
      <TextField
        select
        size="small"
        label="Value"
        value={typeof value === "number" ? value : ""}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={{ minWidth: 200, flex: 1 }}
      >
        {tiers.map((tier) => (
          <MenuItem key={tier} value={tier}>
            {tierLabel(property, tier)}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  // A dynamic property: offer the values actually seen on recent leads, but let
  // the admin type a new one too (freeSolo), since values are free-form.
  if (
    dynamicValues &&
    dynamicValues.length > 0 &&
    operator !== "contains" &&
    operator !== "notContains"
  ) {
    if (isMulti) {
      const selected = Array.isArray(value) ? value.map(String) : []
      return (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={dynamicValues}
          value={selected}
          onChange={(_, values) => onChange(values)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Values"
              placeholder="Select or type…"
            />
          )}
          sx={{ minWidth: 240, flex: 1 }}
        />
      )
    }
    return (
      <Autocomplete
        freeSolo
        size="small"
        options={dynamicValues}
        value={typeof value === "string" ? value : ""}
        onChange={(_, next) => onChange(next ?? "")}
        onInputChange={(_, next) => onChange(next)}
        renderInput={(params) => (
          <TextField {...params} label="Value" placeholder="Select or type…" />
        )}
        sx={{ minWidth: 200, flex: 1 }}
      />
    )
  }

  return (
    <TextField
      size="small"
      label="Value"
      type={isNumericProperty(property) ? "number" : "text"}
      value={
        typeof value === "string" || typeof value === "number" ? value : ""
      }
      onChange={(e) =>
        onChange(
          isNumericProperty(property) ? Number(e.target.value) : e.target.value,
        )
      }
      sx={{ minWidth: 200, flex: 1 }}
    />
  )
}

// Seed the editor's distribution rows from a rule. A rule already using groups
// keeps them. An older rule is converted: one row per tag it gated on (even
// shares), or — with no tag gate — one consultant row per weighted person,
// keeping their relative shares. A brand-new rule starts empty.
function seedGroups(rule: Rule | undefined): EditableGroup[] {
  if (rule && rule.distributionGroups && rule.distributionGroups.length > 0) {
    return fromDistributionGroups(rule.distributionGroups)
  }
  if (!rule) {
    return []
  }
  const gateTags = Array.from(
    new Set(
      (rule.requiredTagConditions ?? [])
        .filter((condition) => condition.mode === "any")
        .flatMap((condition) => condition.tags),
    ),
  )
  if (gateTags.length > 0) {
    const share = 100 / gateTags.length
    return fromDistributionGroups(
      gateTags.map((tag) => ({
        conditions: [{ mode: "any", tags: [tag] }],
        consultantIds: [],
        weight: share,
      })),
    )
  }
  const positives = rule.weights.filter((weight) => weight.weight > 0)
  const total = positives.reduce((sum, weight) => sum + weight.weight, 0) || 1
  return fromDistributionGroups(
    positives.map((weight) => ({
      conditions: [],
      consultantIds: [weight.consultantId],
      weight: (weight.weight / total) * 100,
    })),
  )
}

export function RuleEditorDialog(props: {
  open: boolean
  rule?: Rule
  onClose: () => void
}) {
  const { open, rule, onClose } = props
  const dispatch = useAppDispatch()
  const consultantsState = useAppSelector(selectRoutingConsultants)
  // Stable reference so the reactive-membership effect doesn't re-run every
  // render (its deps include the consultants list).
  const consultants = useMemo(
    () => (consultantsState.status === "success" ? consultantsState.data : []),
    [consultantsState],
  )

  const [name, setName] = useState("")
  const [conditions, setConditions] = useState<EditableCondition[]>([])
  // The rule's distribution as one list of rows (each a tag or a consultant,
  // with a capped share). This is the whole "Routes to" — no separate gate or
  // per-consultant list.
  const [groups, setGroups] = useState<EditableGroup[]>([])
  // Hold rule: matching leads are assigned to nobody (no distribution at all)
  // and wait for manual assignment — they never fall to "Everyone else".
  const [holdLead, setHoldLead] = useState(false)
  const [saving, setSaving] = useState(false)
  const [replay, setReplay] = useState<Replay | null>(null)
  const [replaying, setReplaying] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setName(rule?.name ?? "")
    setConditions(
      rule
        ? rule.conditions.map((c) => ({
            property: c.property,
            operator: c.operator,
            value: c.value,
          }))
        : [],
    )
    setGroups(seedGroups(rule))
    setHoldLead(rule?.holdLead ?? false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule])

  const consultantById = new Map(consultants.map((c) => [c.id, c]))
  // Tags already applied to any consultant — the dropdown options for the
  // rule's required-tags gate (created free-text on the sales page).
  const allTags = Array.from(new Set(consultants.flatMap((c) => c.tags))).sort()

  // Dynamic lead properties discovered from recent leads: keys beyond the
  // curated set become routable automatically, with their observed values.
  const [dynamicProps, setDynamicProps] = useState<DynamicLeadProperty[]>([])
  useEffect(() => {
    if (!open) {
      return
    }
    LeadRoutingClient.listDynamicLeadProperties()
      .then(setDynamicProps)
      .catch(() => setDynamicProps([]))
  }, [open])
  const dynamicValuesByKey = useMemo(() => {
    const map: Record<string, string[]> = {}
    dynamicProps.forEach((property) => {
      map[property.key] = property.values
    })
    return map
  }, [dynamicProps])
  // Property dropdown: the curated known properties, then any dynamic keys not
  // already curated.
  const propertyOptions = useMemo(() => {
    const known = Object.keys(PROPERTY_LABELS)
    const dynamic = dynamicProps
      .map((property) => property.key)
      .filter((key) => !(key in PROPERTY_LABELS))
    return [...known, ...dynamic]
  }, [dynamicProps])

  const nameValid = name.trim().length > 0
  const conditionsValid = conditions.every(conditionComplete)
  // The routed-to rows ARE the distribution: at least one row targeting someone,
  // and the shares totalling 100.
  const validGroups = groups.filter(
    (g) =>
      g.conditions.some((c) => c.tags.length > 0) || g.consultantIds.length > 0,
  )
  const groupsTotal = groups.reduce((s, g) => s + g.weight, 0)
  const groupsValid =
    validGroups.length > 0 && Math.abs(groupsTotal - 100) < 0.5
  // A hold rule has no distribution to validate.
  const canSave =
    nameValid && conditionsValid && (holdLead || groupsValid) && !saving

  const addCondition = () =>
    setConditions((prev) => [
      ...prev,
      { property: "qtf", operator: "in", value: [] },
    ])
  const patchCondition = (index: number, patch: Partial<EditableCondition>) =>
    setConditions((prev) =>
      prev.map((condition, idx) =>
        idx === index ? { ...condition, ...patch } : condition,
      ),
    )
  const removeCondition = (index: number) =>
    setConditions((prev) => prev.filter((_, idx) => idx !== index))

  const buildBody = () => ({
    name: name.trim(),
    conditions: conditions.map(parseCondition),
    // The distribution lives entirely in the rows now; a rule saved from this
    // editor carries no per-consultant weights or separate tag gate. A hold
    // rule carries no distribution at all.
    weights: [],
    requiredTagConditions: [],
    distributionGroups: holdLead ? [] : toDistributionGroups(groups),
    holdLead,
  })

  // Any edit invalidates a previous replay preview.
  useEffect(() => {
    setReplay(null)
  }, [conditions, groups, holdLead])

  const runReplay = async () => {
    setReplaying(true)
    try {
      const body = buildBody()
      setReplay(
        await LeadRoutingClient.replayRule({
          conditions: body.conditions,
          weights: body.weights,
          requiredTagConditions: body.requiredTagConditions,
          distributionGroups: body.distributionGroups,
          holdLead: body.holdLead,
        }),
      )
    } catch (e) {
      dispatch(showErrorSnackbar((e as Error).message))
    } finally {
      setReplaying(false)
    }
  }

  const save = async (thenPublish: boolean) => {
    setSaving(true)
    try {
      const body = buildBody()
      if (rule) {
        await dispatch(updateRule({ id: rule.id, body })).unwrap()
        if (thenPublish) {
          await dispatch(publishRule(rule.id)).unwrap()
        }
      } else {
        await dispatch(createRule(body)).unwrap()
      }
      onClose()
    } catch {
      // errors surface via the snackbar dispatched inside the thunk
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? "Edit rule" : "New rule"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            label="Rule name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            error={!nameValid && name.length > 0}
          />

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Typography variant="subtitle2">When a lead matches</Typography>
              <Tooltip title="All conditions must match (AND). Use a separate rule for an OR. The first rule that matches, top to bottom, wins.">
                <InfoOutlinedIcon fontSize="small" color="disabled" />
              </Tooltip>
            </Stack>
            <Stack spacing={1}>
              {conditions.map((condition, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <TextField
                    select
                    size="small"
                    label="Property"
                    value={condition.property}
                    onChange={(e) => {
                      const property = e.target.value as RuleConditionProperty
                      const operator = operatorsForProperty(property)[0]
                      patchCondition(index, {
                        property,
                        operator,
                        value: defaultValueForOperator(operator),
                      })
                    }}
                    sx={{ minWidth: 150 }}
                  >
                    {propertyOptions.map((property) => (
                      <MenuItem key={property} value={property}>
                        {propertyLabel(property)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Operator"
                    value={condition.operator}
                    onChange={(e) => {
                      const operator = e.target.value as RuleConditionOperator
                      patchCondition(index, {
                        operator,
                        value: coerceValueForOperator(
                          condition.value,
                          operator,
                        ),
                      })
                    }}
                    sx={{ minWidth: 150 }}
                  >
                    {operatorsForProperty(condition.property).map(
                      (operator) => (
                        <MenuItem key={operator} value={operator}>
                          {OPERATOR_LABELS[operator]}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                  <ConditionValueInput
                    condition={condition}
                    dynamicValues={dynamicValuesByKey[condition.property]}
                    onChange={(value) => patchCondition(index, { value })}
                  />
                  <IconButton
                    aria-label="remove condition"
                    onClick={() => removeCondition(index)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addCondition}
                  size="small"
                >
                  Add condition
                </Button>
              </Box>
              {conditions.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No conditions, this rule would match every lead.
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={holdLead}
                    onChange={(e) => setHoldLead(e.target.checked)}
                  />
                }
                label="Don't assign matching leads"
              />
              <Tooltip title="Matching leads are assigned to nobody — they never fall to the 'Everyone else' rotation and wait for manual assignment.">
                <InfoOutlinedIcon fontSize="small" color="disabled" />
              </Tooltip>
            </Stack>
            {holdLead && (
              <Typography variant="caption" color="text.secondary">
                Leads matching this rule are held: no consultant receives them
                until someone assigns them by hand.
              </Typography>
            )}
          </Box>

          {!holdLead && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <Typography variant="subtitle2">Routes to</Typography>
                <Tooltip title="Route this rule's leads to consultants. Each row takes a share (rows total 100%), split evenly among everyone carrying its tag — or one consultant you add by hand. A person in several rows gets the sum of their shares, so overlapping rows (e.g. nested tiers) are fine.">
                  <InfoOutlinedIcon fontSize="small" color="disabled" />
                </Tooltip>
              </Stack>
              <DistributionGroupsEditor
                groups={groups}
                eligible={consultants}
                allTags={allTags}
                onChange={setGroups}
              />
            </Box>
          )}

          <Divider />
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="subtitle2" fontWeight={400}>
                  Preview against recent leads
                </Typography>
                <Tooltip title="A preview, not real results. It replays this draft over the last 30 days of leads to show the split it would produce if it were live.">
                  <InfoOutlinedIcon fontSize="small" color="disabled" />
                </Tooltip>
              </Stack>
              <Button
                size="small"
                onClick={runReplay}
                disabled={(!holdLead && !groupsValid) || replaying}
                startIcon={
                  replaying ? <CircularProgress size={14} /> : undefined
                }
              >
                {replaying ? "Running…" : "Run preview"}
              </Button>
            </Stack>

            {replay && (
              <Box mt={1}>
                <ReplayResultView
                  replay={replay}
                  consultantName={(id) =>
                    consultantById.get(id)?.name ?? id.slice(0, 8)
                  }
                  showTitle={false}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {rule ? (
          <>
            <Button onClick={() => save(false)} disabled={!canSave}>
              Save draft
            </Button>
            <Button
              variant="contained"
              onClick={() => save(true)}
              disabled={!canSave}
            >
              Save &amp; publish
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => save(false)}
            disabled={!canSave}
          >
            Create
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
